import { addDays, dayOf, viewerTimeZone } from '../../../../shared/date/local-time'

// The inbox in days (owner 2026-10-24): Today, Yesterday, Earlier this week,
// Older -- counted on the viewer's calendar in the viewer's zone, like every
// other time in the admin (shared/date/local-time.js). "This week" is the
// viewer's own week: it starts on their locale's first day (Sunday in the
// US, Monday in most of Europe), so on that first day there is no "Earlier
// this week" and yesterday is still Yesterday. A time ahead of the clock
// (skew) reads as Today; an unreadable one as Older. Items keep the order
// they came in (newest first) inside each group.

export const GROUPS = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'week', label: 'Earlier this week' },
  { key: 'older', label: 'Older' }
]

// Regions whose week starts on Sunday, for browsers whose Intl.Locale has no
// week info (Firefox): the common ones; everywhere else, Monday (ISO).
const SUNDAY_FIRST = new Set(['US', 'CA', 'MX', 'BR', 'JP', 'KR', 'TW', 'HK', 'IL', 'PH', 'IN', 'ZA', 'SA', 'PE', 'CO', 'GT', 'VE', 'PR'])

// The first day of the week for a locale, 1 (Monday) .. 7 (Sunday), the way
// Intl.Locale's weekInfo numbers it.
export const weekStartDay = locale => {
  const tag = locale || (typeof navigator !== 'undefined' && navigator.language) || 'en-US'
  try {
    const l = new Intl.Locale(tag)
    const info = typeof l.getWeekInfo === 'function' ? l.getWeekInfo() : l.weekInfo
    if (info && info.firstDay >= 1 && info.firstDay <= 7) return info.firstDay
    const region = l.region || (l.maximize && l.maximize().region)
    return region && SUNDAY_FIRST.has(region) ? 7 : 1
  } catch (e) {
    return 1
  }
}

const isoWeekday = day => {
  const [y, m, d] = day.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay() || 7
}

// The first day (YYYY-MM-DD) of the week holding `today`.
export const weekStartOf = (today, weekStart) => addDays(today, -((isoWeekday(today) - weekStart + 7) % 7))

// Which group an instant belongs to.
export const groupKeyOf = (value, { now = Date.now(), tz = viewerTimeZone(), weekStart = weekStartDay() } = {}) => {
  const day = value ? dayOf(value, tz) : ''
  if (!day) return 'older'
  const today = dayOf(now, tz)
  if (day >= today) return 'today'
  if (day === addDays(today, -1)) return 'yesterday'
  if (day >= weekStartOf(today, weekStart)) return 'week'
  return 'older'
}

// [{ key, label, items }] for the groups that have items, in order.
export const groupByDay = (items, opts = {}) => {
  const now = opts.now === undefined ? Date.now() : opts.now
  const tz = opts.tz || viewerTimeZone()
  const weekStart = opts.weekStart || weekStartDay()
  const by = new Map(GROUPS.map(g => [g.key, []]))
  ;(items || []).forEach(item => by.get(groupKeyOf(item && item.createdAt, { now, tz, weekStart })).push(item))
  return GROUPS.filter(g => by.get(g.key).length > 0).map(g => ({ ...g, items: by.get(g.key) }))
}
